import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { resolveTeam, ShareButton, PullToRefresh, MatchGroupSkeleton, Ico, Icons, TennisBallIcon, PlayerAvatar, AddToCalendar } from '../../components/common';
import { tournamentAPI, createMatchSSE } from '../../services/api';
import { formatDateIST, isTodayIST } from '../../utils/dateUtils';
import { useNavigation } from '../../context/NavigationContext';

export function LiveScoresContent({ embedded = false }) {
  const { savePageState, getPageState } = useNavigation();
  const [matches, setMatches] = useState([]);
  const [players, setPlayers] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [tab, setTab] = useState(() => getPageState('tab', null));
  const [filterT, setFilterT] = useState(() => getPageState('filterT', 'all'));
  const [search, setSearch] = useState(() => getPageState('search', ''));
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [hasSetDefaultTab, setHasSetDefaultTab] = useState(false);
  const [expandedRounds, setExpandedRounds] = useState({});

  // Save state on changes
  useEffect(() => {
    if (tab) savePageState('tab', tab);
  }, [tab, savePageState]);

  useEffect(() => {
    savePageState('filterT', filterT);
  }, [filterT, savePageState]);

  useEffect(() => {
    savePageState('search', search);
  }, [search, savePageState]);

  const loadData = async () => {
    try {
      const res = await tournamentAPI.getLiveSummary();
      setTournaments(res.data.tournaments || []);
      // Filter out bye matches (matches without both players)
      const allMatches = [...(res.data.liveMatches || []), ...(res.data.upcomingMatches || []), ...(res.data.recentMatches || [])];
      const validMatches = allMatches.filter(m => m.player1 && m.player2 && m.player1 !== 'BYE' && m.player2 !== 'BYE');
      setMatches(validMatches);
      setPlayers(res.data.players || []);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  // SSE for real-time updates, fallback to 30s polling
  useEffect(() => {
    let interval;
    const es = createMatchSSE(() => loadData());
    es.onerror = () => {
      es.close();
      interval = setInterval(loadData, 30000);
    };
    return () => { es.close(); if (interval) clearInterval(interval); };
  }, []);

  // Set default tab based on available content (only once on initial load)
  useEffect(() => {
    if (!loading && !hasSetDefaultTab) {
      const hasLive = matches.some(m => m.status === 'live');
      const hasToday = matches.some(m => m.date && isTodayIST(m.date));
      const hasUpcoming = matches.some(m => m.status === 'scheduled' || m.status === 'pending');
      const hasResults = matches.some(m => m.status === 'completed');
      
      if (hasLive) setTab('live');
      else if (hasToday) setTab('today');
      else if (hasUpcoming) setTab('upcoming');
      else if (hasResults) setTab('results');
      else setTab('live');
      setHasSetDefaultTab(true);
    }
  }, [loading, matches, hasSetDefaultTab]);

  // Auto-expand all rounds when tournament filter or tab changes
  useEffect(() => {
    if (filterT !== 'all') {
      // Auto-expand all rounds for better UX
      const allRounds = {};
      matches.forEach(m => {
        if (m.round) allRounds[m.round] = true;
      });
      setExpandedRounds(allRounds);
    }
  }, [filterT, tab, matches]);

  const handleRefresh = useCallback(() => new Promise(async resolve => {
    await loadData();
    setRefreshKey(k => k + 1);
    resolve();
  }), []);

  const ongoingTourneys = tournaments;
  const ongoingIds = new Set(ongoingTourneys.map(t => t.id || t._id));

  const allLive = useMemo(() => 
    matches.filter(m => m.status === 'live')
      .sort((a, b) => new Date(b.updatedAt || '1970-01-01') - new Date(a.updatedAt || '1970-01-01')),
    [matches]
  );
  
  const allToday = useMemo(() => 
    matches.filter(m => m.date && isTodayIST(m.date))
      .sort((a, b) => {
        // Live first, then by time
        if (a.status === 'live' && b.status !== 'live') return -1;
        if (a.status !== 'live' && b.status === 'live') return 1;
        const hasA = a.time, hasB = b.time;
        if (hasA && !hasB) return -1;
        if (!hasA && hasB) return 1;
        if (!hasA && !hasB) return 0;
        return a.time.localeCompare(b.time);
      }),
    [matches]
  );
  
  const allRecent = useMemo(() => 
    matches.filter(m => m.status === 'completed')
      .sort((a, b) => new Date(b.updatedAt || '1970-01-01') - new Date(a.updatedAt || '1970-01-01')),
    [matches]
  );
  
  const allScheduled = useMemo(() => 
    matches.filter(m => m.status === 'scheduled' || m.status === 'pending')
      .sort((a, b) => {
        const hasA = a.date && a.time, hasB = b.date && b.time;
        if (hasA && !hasB) return -1;
        if (!hasA && hasB) return 1;
        if (!hasA && !hasB) return 0;
        return new Date(`${a.date}T${a.time}`) - new Date(`${b.date}T${b.time}`);
      }),
    [matches]
  );

  const activeTourneyIds = new Set(matches.map(m => m.tournamentId));
  const activeTourneys = ongoingTourneys
    .filter(t => activeTourneyIds.has(t.id))
    .sort((a, b) => new Date(b.startDate || '1970-01-01') - new Date(a.startDate || '1970-01-01'));

  const applyFilter = useCallback((list) => {
    let filtered = filterT === 'all' ? list : list.filter(m => m.tournamentId === filterT);
    if (search.trim()) {
      const q = search.toLowerCase();
      filtered = filtered.filter(m => {
        const t1 = resolveTeam(m.player1, players);
        const t2 = resolveTeam(m.player2, players);
        return t1.name.toLowerCase().includes(q) || t2.name.toLowerCase().includes(q);
      });
    }
    return filtered;
  }, [filterT, search, players]);

  const live = useMemo(() => applyFilter(allLive), [applyFilter, allLive]);
  const today = useMemo(() => applyFilter(allToday), [applyFilter, allToday]);
  const recent = useMemo(() => applyFilter(allRecent), [applyFilter, allRecent]);
  const scheduled = useMemo(() => applyFilter(allScheduled), [applyFilter, allScheduled]);

  // Auto-switch tab when tournament filter changes and current tab has no data
  useEffect(() => {
    if (filterT !== 'all' && tab) {
      const currentList = tab === 'live' ? live : tab === 'today' ? today : tab === 'upcoming' ? scheduled : recent;
      
      // If current tab has no matches, switch to first tab with data
      if (currentList.length === 0) {
        if (live.length > 0) setTab('live');
        else if (today.length > 0) setTab('today');
        else if (scheduled.length > 0) setTab('upcoming');
        else if (recent.length > 0) setTab('results');
      }
    }
  }, [filterT, tab, live, today, scheduled, recent]);

  const tabList = [
    { key: 'live', label: 'Live', count: live.length, dot: true },
    { key: 'today', label: 'Today', count: today.length },
    { key: 'upcoming', label: 'Upcoming', count: scheduled.length },
    { key: 'results', label: 'Complete', count: recent.length },
  ].filter(t => t.count > 0 || filterT === 'all'); // Show all tabs when 'All' is selected

  const currentList = tab === 'live' ? live : tab === 'today' ? today : tab === 'upcoming' ? scheduled : recent;
  
  // Smart grouping: by round if single tournament selected, otherwise by tournament
  const grouped = {};
  if (filterT !== 'all') {
    // Single tournament: group by round
    // Proper tournament progression order (earliest to latest)
    const roundOrder = ['Round of 128', 'Round of 64', 'Round of 32', 'Round of 16', 'Quarter Final', 'Semi Final', 'Final'];
    currentList.forEach(m => {
      const key = m.round || 'Other';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(m);
    });
    // Sort rounds in chronological order (earliest rounds first)
    const sortedKeys = Object.keys(grouped).sort((a, b) => {
      const ai = roundOrder.indexOf(a), bi = roundOrder.indexOf(b);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a.localeCompare(b);
    });
    const sortedGrouped = {};
    sortedKeys.forEach(k => sortedGrouped[k] = grouped[k]);
    Object.keys(grouped).forEach(k => delete grouped[k]);
    Object.assign(grouped, sortedGrouped);
  } else {
    // Multiple tournaments: group by tournament
    currentList.forEach(m => { 
      if (!grouped[m.tournamentId]) grouped[m.tournamentId] = []; 
      grouped[m.tournamentId].push(m); 
    });
  }

  const emptyMsg = {
    live: [<TennisBallIcon size={40} />, 'No Live Matches', 'Check today or upcoming tab'],
    today: [<Ico d={Icons.calendar} size={36} color="var(--gray-300)" />, 'No Matches Today', 'Check upcoming matches'],
    upcoming: [<Ico d={Icons.calendar} size={36} color="var(--gray-300)" />, 'No Upcoming Matches', 'Schedule will be updated soon'],
    results: [<Ico d={Icons.trophy} size={36} color="var(--gray-300)" />, 'No Results Yet', 'Completed matches will appear here'],
  };

  if (embedded) {
    // Don't render anything in embedded mode if there's no content at all
    if (!loading && live.length === 0 && scheduled.length === 0 && recent.length === 0) {
      return null;
    }

    return (
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h2 className="heading">LIVE SCORES</h2>
          <Link to="/live" style={{ fontSize: 12, color: '#1E88E5', fontWeight: 600, textDecoration: 'none' }}>View All →</Link>
        </div>
        <div className="pill-tabs" style={{ marginBottom: 12 }}>
          {tabList.map(t => (
            <button key={t.key} className={`pill-tab ${tab === t.key ? 'pill-tab-active' : ''}`} onClick={() => setTab(t.key)}>
              {t.dot && tab !== t.key && <span className="live-dot" />}
              {t.label}
              <span style={{ fontSize: 10, fontWeight: 700, background: tab === t.key ? 'rgba(189,255,0,0.2)' : 'var(--gray-100)', color: tab === t.key ? 'var(--lime)' : 'var(--gray-400)', padding: '1px 6px', borderRadius: 10, minWidth: 18, textAlign: 'center', marginLeft: 4 }}>{t.count}</span>
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {loading || !hasSetDefaultTab ? (
            <MatchGroupSkeleton rows={3} />
          ) : currentList.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">{emptyMsg[tab][0]}</div>
              <h3 className="empty-title">{emptyMsg[tab][1]}</h3>
              <p className="empty-sub">{emptyMsg[tab][2]}</p>
            </div>
          ) : (
            <div>
              {Object.entries(grouped).map(([key, ms]) => {
                const isTournamentGroup = filterT === 'all';
                const t = isTournamentGroup ? tournaments.find(t => (t.id || t._id) === key) : tournaments.find(t => t.id === filterT);
                const hasMore = ms.length > 6 && tab !== 'results';
                const displayMatches = tab === 'results' ? ms : ms.slice(0, 6);
                return (
                  <div key={key}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                      {tab === 'live' && <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--red)', animation: 'pulse 1.5s infinite', flexShrink: 0 }} />}
                      {!isTournamentGroup && <div style={{ width: 3, height: 12, background: '#00205B', borderRadius: 2, flexShrink: 0 }} />}
                      <span className="font-display" style={{ fontSize: 11, fontWeight: 800, color: 'var(--navy)', letterSpacing: 1.5, textTransform: 'uppercase' }}>
                        {isTournamentGroup ? t?.name : key}
                      </span>
                      {!isTournamentGroup && <span style={{ fontSize: 9, fontWeight: 600, color: '#8892A6', background: '#F8F9FC', padding: '2px 6px', borderRadius: 4 }}>{ms.length}</span>}
                      <div style={{ flex: 1, height: 1, background: 'var(--gray-100)' }} />
                      <span style={{ fontSize: 10, color: 'var(--gray-400)', fontWeight: 600 }}>{t?.surface} · {t?.matchType || 'Singles'}</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12, marginBottom: hasMore ? 8 : 16 }}>
                      {displayMatches.map(m => (tab === 'live' || (tab === 'today' && m.status === 'live'))
                        ? <LiveMatchCard key={m.id || m._id} m={m} players={players} />
                        : <MatchRow key={m.id || m._id} m={m} players={players} />
                      )}
                    </div>
                    {hasMore && (
                      <Link to="/matches" style={{ display: 'block', textAlign: 'center', padding: '6px', background: 'var(--gray-50)', borderRadius: 6, fontSize: 10, fontWeight: 600, color: 'var(--navy)', textDecoration: 'none', marginBottom: 16, border: '1px solid var(--gray-200)' }}>
                        +{ms.length - 6} more
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="page-shell">
      <div className="page-header">
        <div className="container-xs">
          <h1 className="heading" style={{ marginBottom: 8 }}>MATCHES</h1>
          <div style={{ position: 'relative', marginBottom: 8 }}>
            <svg style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8892A6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/>
              <path d="m21 21-4.35-4.35"/>
            </svg>
            <input 
              className="form-input" 
              style={{ width: '100%', padding: '12px 0 12px 32px', fontSize: 14, border: 0, borderBottom: '1px solid #E5E8EF', borderRadius: 0, background: 'transparent', transition: 'border-color 0.2s', outline: 0, boxShadow: 'none' }} 
              placeholder="Search player..." 
              value={search} 
              onChange={e => setSearch(e.target.value)}
              onFocus={e => { e.target.style.borderBottom = '2px solid #00205B'; e.target.style.outline = 'none'; e.target.style.boxShadow = 'none'; }}
              onBlur={e => { e.target.style.borderBottom = '1px solid #E5E8EF'; e.target.style.boxShadow = 'none'; }}
            />
          </div>
          <div className="pill-tabs" style={{ marginBottom: 6 }}>
            <button className={`pill-tab ${filterT === 'all' ? 'pill-tab-active' : ''}`} onClick={() => setFilterT('all')}>All</button>
            {activeTourneys.map(t => (
              <button key={t.id} className={`pill-tab ${filterT === t.id ? 'pill-tab-active' : ''}`} onClick={() => setFilterT(t.id)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {t.name}
                {filterT === t.id && (
                  <Link 
                    to={`/tournaments/${t.id}`}
                    onClick={(e) => e.stopPropagation()}
                    style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textDecoration: 'none'
                    }}
                    title="View Tournament"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#BDFF00" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M7 17L17 7M17 7H7M17 7v10"/>
                    </svg>
                  </Link>
                )}
              </button>
            ))}
          </div>
          <div className="tabs" style={{ marginBottom: 0 }}>
            {tabList.map(t => (
              <button key={t.key} className={`tab ${tab === t.key ? 'tab-active' : ''}`} onClick={() => setTab(t.key)}>
                {t.dot && tab !== t.key && <span className="live-dot" />}
                {t.label}
                <span className={`tab-count ${tab === t.key ? 'tab-count-active' : ''}`}>{t.count}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <PullToRefresh onRefresh={handleRefresh} className="page-body" style={{ overflowY: 'auto', paddingTop: 8 }}>
        <div className="container-xs" key={refreshKey}>
          {loading || !hasSetDefaultTab ? (
            <><MatchGroupSkeleton rows={2} /><div style={{ height: 14 }} /><MatchGroupSkeleton rows={3} /></>
          ) : currentList.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">{emptyMsg[tab][0]}</div>
              <h3 className="empty-title">{emptyMsg[tab][1]}</h3>
              <p className="empty-sub">{emptyMsg[tab][2]}</p>
            </div>
          ) : filterT !== 'all' ? (
            // Compact Timeline view for single tournament (mobile-optimized)
            <div style={{ position: 'relative', paddingLeft: 28 }}>
              {/* Vertical timeline line */}
              <div style={{ position: 'absolute', left: 9, top: 0, bottom: 0, width: 2, background: 'linear-gradient(180deg, #00205B, #1E88E5, #00205B)', borderRadius: 1 }} />
              {Object.entries(grouped).map(([key, ms], idx) => {
                const t = tournaments.find(t => t.id === filterT);
                const isLast = idx === Object.keys(grouped).length - 1;
                const hasLive = tab === 'live' && ms.some(m => m.status === 'live');
                const isExpanded = expandedRounds[key] !== false; // Default to expanded
                const toggleRound = () => setExpandedRounds(prev => ({ ...prev, [key]: !isExpanded }));
                return (
                  <div key={key} style={{ position: 'relative', marginBottom: isLast ? 0 : 20 }}>
                    {/* Timeline node - Tennis ball icon (clickable for collapse/expand) */}
                    <div 
                      onClick={toggleRound}
                      style={{ 
                        position: 'absolute', 
                        left: -27, 
                        top: 0, 
                        width: 18, 
                        height: 18, 
                        borderRadius: '50%', 
                        background: isExpanded ? 'radial-gradient(circle at 35% 35%, #1E3A5F, #00205B 50%, #001840)' : 'radial-gradient(circle at 35% 35%, #5F6B80, #8892A6)', 
                        boxShadow: isExpanded ? '0 2px 6px rgba(0,0,0,0.2), 0 0 0 2px rgba(189,255,0,0.3)' : '0 2px 4px rgba(0,0,0,0.15)', 
                        zIndex: 2, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {/* Tennis ball curve lines in lime green */}
                      <svg width="18" height="18" viewBox="0 0 18 18" style={{ position: 'absolute', opacity: isExpanded ? 1 : 0.5, transition: 'opacity 0.2s' }}>
                        <path d="M 3 3 Q 9 9 3 15" stroke="#BDFF00" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                        <path d="M 15 3 Q 9 9 15 15" stroke="#BDFF00" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                      </svg>
                    </div>
                    {/* Round header - clickable to collapse/expand */}
                    <div style={{ marginBottom: isExpanded ? 10 : 0, cursor: 'pointer' }} onClick={toggleRound}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        {hasLive && <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--red)', animation: 'pulse 1.5s infinite', flexShrink: 0 }} />}
                        <span className="font-display" style={{ fontSize: 11, fontWeight: 800, color: 'var(--navy)', letterSpacing: 1.5, textTransform: 'uppercase', paddingLeft: (key.includes('Round of') ? 4 : 0) }}>{key}</span>
                        <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', background: '#00205B', padding: '2px 7px', borderRadius: 10 }}>{ms.length}</span>
                        {hasLive && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 9, fontWeight: 800, color: '#E53935', background: '#FFEBEE', padding: '2px 6px', borderRadius: 8 }}>
                            <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#E53935', animation: 'pulse 1.5s infinite' }} />
                            LIVE
                          </span>
                        )}
                      </div>
                      {isExpanded && (
                        <div style={{ fontSize: 10, color: 'var(--gray-400)', fontWeight: 600, marginTop: 2, paddingLeft: (key.includes('Round of') ? 4 : 0) }}>{t?.surface} · {t?.matchType || 'Singles'}</div>
                      )}
                    </div>
                    {/* Matches grid - collapsible */}
                    {isExpanded && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
                        {ms.map(m => (tab === 'live' || (tab === 'today' && m.status === 'live'))
                          ? <LiveMatchCard key={m.id} m={m} players={players} />
                          : <MatchRow key={m.id} m={m} players={players} />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {/* End ball icon */}
              <div style={{
                position: 'absolute',
                left: 1,
                bottom: -20,
                width: 18,
                height: 18,
                borderRadius: '50%',
                background: '#43A047',
                border: '2px solid #fff',
                boxShadow: '0 0 0 3px rgba(67,160,71,0.2)',
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2z" stroke="#fff" strokeWidth="2" fill="none" />
                  <path d="M12 2c0 5.5-4.5 10-10 10" stroke="#fff" strokeWidth="1.5" fill="none" />
                  <path d="M12 22c0-5.5 4.5-10 10-10" stroke="#fff" strokeWidth="1.5" fill="none" />
                </svg>
              </div>
            </div>
          ) : (
            // Tournament grouping for 'All' filter with timeline
            <div style={{ position: 'relative', paddingLeft: 28 }}>
              {/* Vertical timeline line */}
              <div style={{ position: 'absolute', left: 9, top: 0, bottom: 0, width: 2, background: 'linear-gradient(180deg, #00205B, #1E88E5, #00205B)', borderRadius: 1 }} />
              {Object.entries(grouped).map(([key, ms], idx) => {
                const t = tournaments.find(t => t.id === key);
                const isLast = idx === Object.keys(grouped).length - 1;
                const hasLive = tab === 'live' && ms.some(m => m.status === 'live');
                const isExpanded = expandedRounds[key] !== false;
                const toggleTournament = () => setExpandedRounds(prev => ({ ...prev, [key]: !isExpanded }));
                return (
                  <div key={key} style={{ position: 'relative', marginBottom: isLast ? 0 : 20 }}>
                    {/* Timeline node - Tennis ball icon (clickable for collapse/expand) */}
                    <div 
                      onClick={toggleTournament}
                      style={{ 
                        position: 'absolute', 
                        left: -27, 
                        top: 0, 
                        width: 18, 
                        height: 18, 
                        borderRadius: '50%', 
                        background: isExpanded ? 'radial-gradient(circle at 35% 35%, #1E3A5F, #00205B 50%, #001840)' : 'radial-gradient(circle at 35% 35%, #5F6B80, #8892A6)', 
                        boxShadow: isExpanded ? '0 2px 6px rgba(0,0,0,0.2), 0 0 0 2px rgba(189,255,0,0.3)' : '0 2px 4px rgba(0,0,0,0.15)', 
                        zIndex: 2, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {/* Tennis ball curve lines */}
                      <svg width="18" height="18" viewBox="0 0 18 18" style={{ position: 'absolute', opacity: isExpanded ? 1 : 0.5, transition: 'opacity 0.2s' }}>
                        <path d="M 3 3 Q 9 9 3 15" stroke="#BDFF00" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                        <path d="M 15 3 Q 9 9 15 15" stroke="#BDFF00" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                      </svg>
                    </div>
                    {/* Tournament header - clickable */}
                    <div style={{ marginBottom: isExpanded ? 10 : 0, cursor: 'pointer' }} onClick={toggleTournament}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        {tab === 'live' && hasLive && <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--red)', animation: 'pulse 1.5s infinite', flexShrink: 0 }} />}
                        <span className="font-display" style={{ fontSize: 11, fontWeight: 800, color: 'var(--navy)', letterSpacing: 1.5, textTransform: 'uppercase' }}>
                          {t?.name}
                        </span>
                        <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', background: '#00205B', padding: '2px 7px', borderRadius: 10 }}>{ms.length}</span>
                      </div>
                      {isExpanded && (
                        <div style={{ fontSize: 10, color: 'var(--gray-400)', fontWeight: 600, marginTop: 2 }}>{t?.surface} · {t?.matchType || 'Singles'}</div>
                      )}
                    </div>
                    {/* Matches grid - collapsible */}
                    {isExpanded && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
                        {ms.map(m => (tab === 'live' || (tab === 'today' && m.status === 'live'))
                          ? <LiveMatchCard key={m.id} m={m} players={players} />
                          : <MatchRow key={m.id} m={m} players={players} />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {/* End ball icon */}
              <div style={{
                position: 'absolute',
                left: 1,
                bottom: -10,
                width: 18,
                height: 18,
                borderRadius: '50%',
                background: '#43A047',
                border: '2px solid #fff',
                boxShadow: '0 0 0 3px rgba(67,160,71,0.2)',
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2z" stroke="#fff" strokeWidth="2" fill="none" />
                  <path d="M12 2c0 5.5-4.5 10-10 10" stroke="#fff" strokeWidth="1.5" fill="none" />
                  <path d="M12 22c0-5.5 4.5-10 10-10" stroke="#fff" strokeWidth="1.5" fill="none" />
                </svg>
              </div>
            </div>
          )}
        </div>
      </PullToRefresh>
    </div>
  );
}

function LiveMatchCard({ m, players }) {
  const t1 = resolveTeam(m.player1, players);
  const t2 = resolveTeam(m.player2, players);
  const p1obj = players.find(p => p.id === m.player1);
  const p2obj = players.find(p => p.id === m.player2);
  const sets = m.score?.sets || [];
  const lastIdx = sets.length - 1;
  const cardRef = React.useRef(null);

  const g1 = sets.reduce((s, sc) => s + (sc.p1 || 0), 0);
  const g2 = sets.reduce((s, sc) => s + (sc.p2 || 0), 0);

  const handleShare = async (e) => {
    e.preventDefault(); e.stopPropagation();
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(cardRef.current, { 
        scale: 2, 
        useCORS: true, 
        backgroundColor: '#FFFFFF',
        logging: false,
        windowWidth: cardRef.current.scrollWidth,
        windowHeight: cardRef.current.scrollHeight
      });
      canvas.toBlob(async (blob) => {
        const file = new File([blob], 'live-match.png', { type: 'image/png' });
        if (navigator.canShare?.({ files: [file] })) {
          await navigator.share({ files: [file], title: `${t1.name} vs ${t2.name} 🔴 LIVE` });
        } else {
          const a = document.createElement('a');
          a.href = URL.createObjectURL(blob);
          a.download = 'live-match.png';
          a.click();
        }
      }, 'image/png');
    } catch {}
  };

  const renderPlayer = (team, pobj, pKey, oKey) => {
    const isLeading = sets.length > 0 && (pKey === 'p1' ? g1 > g2 : g2 > g1);
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: isLeading ? 'rgba(189,255,0,0.05)' : 'transparent' }}>
        <PlayerAvatar name={team.name} photo={pobj?.photo} size={28} />
        <span style={{ flex: 1, fontSize: 12, fontWeight: isLeading ? 700 : 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {team.name}
        </span>
        <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
          {sets.map((sc, i) => {
            const mine = sc[pKey], opp = sc[oKey];
            const isCurrent = i === lastIdx;
            const won = mine > opp;
            return (
              <span key={i} style={{
                width: isCurrent ? 30 : 24, height: isCurrent ? 30 : 24,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: isCurrent ? 15 : 12,
                fontWeight: isCurrent ? 800 : won ? 700 : 400,
                fontFamily: "'Outfit', sans-serif",
                color: isCurrent ? '#E53935' : won ? 'var(--navy)' : 'var(--gray-300)',
                background: isCurrent ? 'rgba(229,57,53,0.1)' : won ? 'var(--gray-100)' : 'transparent',
                borderRadius: 6,
                border: isCurrent ? '1.5px solid rgba(229,57,53,0.3)' : '1.5px solid transparent',
              }}>{mine}</span>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Link to={`/matches/${m.id}`} style={{ textDecoration: 'none' }}>
      <div ref={cardRef} style={{ background: 'var(--card-bg)', borderRadius: 12, border: '1.5px solid #E53935', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 10px', background: 'var(--gray-50)', borderBottom: '1px solid var(--card-border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#E53935', animation: 'pulse 1.5s infinite', display: 'inline-block' }} />
            <span style={{ fontSize: 10, fontWeight: 800, color: '#E53935', letterSpacing: 1 }}>LIVE</span>
            {sets.length > 0 && <span style={{ fontSize: 9, color: 'var(--gray-400)', fontWeight: 600, background: 'var(--gray-100)', padding: '1px 6px', borderRadius: 3 }}>Set {sets.length}</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {m.round && <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--navy)', background: 'var(--gray-100)', padding: '2px 6px', borderRadius: 3 }}>{m.round}</span>}
            {m.court && <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 9, color: 'var(--gray-400)', fontWeight: 600 }}><Ico d={Icons.racket} size={9} color="var(--gray-400)" />{m.court}</span>}
          </div>
        </div>
        {/* Players */}
        <div>
          {renderPlayer(t1, p1obj, 'p1', 'p2')}
          <div style={{ height: 1, background: 'var(--card-border)', margin: '0 16px' }} />
          {renderPlayer(t2, p2obj, 'p2', 'p1')}
        </div>
        {/* Footer */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 10px', borderTop: '1px solid #EEF1F6' }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {sets.map((_, i) => (
              <span key={i} style={{ fontSize: 8, fontWeight: 700, color: i === lastIdx ? '#E53935' : '#B0B8C9', letterSpacing: 0.5 }}>S{i + 1}{i === lastIdx ? ' ●' : ''}</span>
            ))}
          </div>
          <button onClick={handleShare} className="share-btn-compact" title="Share as Image">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
            </svg>
          </button>
        </div>
      </div>
    </Link>
  );
}

function MatchRow({ m, players }) {
  const t1 = resolveTeam(m.player1, players);
  const t2 = resolveTeam(m.player2, players);
  const p1obj = players.find(p => p.id === m.player1);
  const p2obj = players.find(p => p.id === m.player2);
  const isDone = m.status === 'completed';
  const isUpcoming = m.status === 'scheduled' || m.status === 'pending';
  const sets = m.score.sets;
  const cardRef = React.useRef(null);

  const handleShare = async (e) => {
    e.preventDefault(); e.stopPropagation();
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(cardRef.current, { 
        scale: 2, 
        useCORS: true, 
        backgroundColor: '#FFFFFF',
        logging: false,
        windowWidth: cardRef.current.scrollWidth,
        windowHeight: cardRef.current.scrollHeight
      });
      canvas.toBlob(async (blob) => {
        const file = new File([blob], 'match-result.png', { type: 'image/png' });
        if (navigator.canShare?.({ files: [file] })) {
          await navigator.share({ files: [file], title: `${t1.name} vs ${t2.name} - Result` });
        } else {
          const a = document.createElement('a');
          a.href = URL.createObjectURL(blob);
          a.download = 'match-result.png';
          a.click();
        }
      }, 'image/png');
    } catch {}
  };

  const renderPlayer = (team, pobj, pKey, oKey) => {
    const isWinner = m.winner === (pKey === 'p1' ? m.player1 : m.player2);
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: isWinner ? 'rgba(67,160,71,0.08)' : 'transparent', borderLeft: isWinner ? '3px solid #43A047' : '3px solid transparent' }}>
        <PlayerAvatar name={team.name} photo={pobj?.photo} size={28} />
        <span style={{ flex: 1, fontSize: 12, fontWeight: isWinner ? 700 : 500, color: isWinner ? '#43A047' : 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {team.name}
        </span>
        {isWinner && (
          <span style={{ fontSize: 8, fontWeight: 800, color: '#fff', background: '#43A047', padding: '2px 6px', borderRadius: 3, letterSpacing: 0.5 }}>WON</span>
        )}
        <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
          {sets.map((sc, i) => {
            const mine = sc[pKey], opp = sc[oKey];
            const won = mine > opp;
            return (
              <span key={i} style={{
                width: 24, height: 24,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12,
                fontWeight: won ? 700 : 400,
                fontFamily: "'Outfit', sans-serif",
                color: won ? 'var(--navy)' : 'var(--gray-300)',
                background: won ? 'var(--gray-100)' : 'transparent',
                borderRadius: 6,
              }}>{mine}</span>
            );
          })}
        </div>
      </div>
    );
  };

  if (isUpcoming) {
    const handleCalendarClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const title = `🎾 ${t1.name} vs ${t2.name}`;
      const dt = new Date(`${m.date}T${m.time || '00:00'}:00`);
      const end = new Date(dt.getTime() + 2 * 60 * 60 * 1000);
      const fmt = d => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
      const ics = [
        'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//TennisBuzz//EN',
        'BEGIN:VEVENT',
        `DTSTART:${fmt(dt)}`, `DTEND:${fmt(end)}`,
        `SUMMARY:${title}`, `LOCATION:${m.court || ''}`,
        `DESCRIPTION:${m.round || ''}\n\nTennisBuzz`,
        'END:VEVENT', 'END:VCALENDAR',
      ].join('\r\n');
      const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `match-${m.id}.ics`;
      a.click();
      URL.revokeObjectURL(url);
    };

    return (
      <Link to={`/matches/${m.id}`} style={{ textDecoration: 'none' }}>
        <div style={{ background: 'var(--card-bg)', borderRadius: 12, border: '2px solid #00205B', overflow: 'hidden' }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 10px', background: 'var(--gray-50)', borderBottom: '1px solid var(--card-border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 9, color: 'var(--gray-400)', fontWeight: 600, background: 'var(--gray-100)', padding: '1px 6px', borderRadius: 3 }}>Upcoming</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {m.round && <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--navy)', background: 'var(--gray-100)', padding: '2px 6px', borderRadius: 3 }}>{m.round}</span>}
              {m.court && <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 9, color: 'var(--gray-400)', fontWeight: 600 }}><Ico d={Icons.racket} size={9} color="var(--gray-400)" />{m.court}</span>}
            </div>
          </div>
          {/* Players */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px' }}>
              <PlayerAvatar name={t1.name} photo={p1obj?.photo} size={28} />
              <span style={{ flex: 1, fontSize: 12, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {t1.name}
              </span>
            </div>
            <div style={{ height: 1, background: 'var(--card-border)', margin: '0 10px' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px' }}>
              <PlayerAvatar name={t2.name} photo={p2obj?.photo} size={28} />
              <span style={{ flex: 1, fontSize: 12, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {t2.name}
              </span>
            </div>
          </div>
          {/* Footer */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 10px', borderTop: '1px solid var(--card-border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 3, background: 'var(--gray-50)', padding: '2px 6px', borderRadius: 5, border: '1px solid var(--gray-200)' }}>
              {m.date && <><Ico d={Icons.calendar} size={8} color='var(--navy)' />
              <span style={{ fontSize: 9, fontWeight: 600, color: 'var(--gray-500)' }}>{formatDateIST(m.date, { weekday: 'short', month: 'short', day: 'numeric' })}</span></>}
              {m.time && <span style={{ fontSize: 9, fontWeight: 800, color: 'var(--navy)', fontFamily: 'var(--font-display)' }}>{m.time}</span>}
            </div>
            <button onClick={handleCalendarClick} className="share-btn-compact" title="Add to Calendar">
              <Ico d={Icons.calendar} size={14} color="currentColor" />
            </button>
          </div>
        </div>
      </Link>
    );
  }

  // Results card - matching LiveMatchCard design
  return (
    <Link to={`/matches/${m.id}`} style={{ textDecoration: 'none' }}>
      <div ref={cardRef} style={{ background: 'var(--card-bg)', borderRadius: 12, border: '1.5px solid #43A047', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 10px', background: 'var(--gray-50)', borderBottom: '1px solid var(--card-border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {sets.length > 0 && <span style={{ fontSize: 9, color: 'var(--gray-400)', fontWeight: 600, background: 'var(--gray-100)', padding: '1px 6px', borderRadius: 3 }}>{sets.length} Set{sets.length > 1 ? 's' : ''}</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {m.round && <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--navy)', background: 'var(--gray-100)', padding: '2px 6px', borderRadius: 3 }}>{m.round}</span>}
            {m.court && <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 9, color: 'var(--gray-400)', fontWeight: 600 }}><Ico d={Icons.racket} size={9} color="var(--gray-400)" />{m.court}</span>}
          </div>
        </div>
        {/* Players */}
        <div>
          {renderPlayer(t1, p1obj, 'p1', 'p2')}
          <div style={{ height: 1, background: 'var(--card-border)', margin: '0 10px' }} />
          {renderPlayer(t2, p2obj, 'p2', 'p1')}
        </div>
        {/* Footer */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 10px', borderTop: '1px solid var(--card-border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 3, background: 'var(--gray-50)', padding: '2px 6px', borderRadius: 5, border: '1px solid var(--gray-200)' }}>
            {m.date && <><Ico d={Icons.calendar} size={8} color='var(--navy)' />
            <span style={{ fontSize: 9, fontWeight: 600, color: 'var(--gray-500)' }}>{formatDateIST(m.date, { weekday: 'short', month: 'short', day: 'numeric' })}</span></>}
            {m.time && <span style={{ fontSize: 9, fontWeight: 800, color: 'var(--navy)', fontFamily: 'var(--font-display)' }}>{m.time}</span>}
          </div>
          <button onClick={handleShare} className="share-btn-compact" title="Share as Image">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
            </svg>
          </button>
        </div>
      </div>
    </Link>
  );
}

const S = {
  statusCol: { width: 64, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, padding: '10px 0', borderRight: '1px solid var(--card-border)', flexShrink: 0 },
  matchCol: { flex: 1, padding: '8px 14px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 6 },
  courtCol: { display: 'flex', alignItems: 'center', padding: '0 12px', borderLeft: '1px solid var(--card-border)' },
};


export default function LiveScores() {
  return <LiveScoresContent embedded={false} />;
}

// Export as Matches for the new unified route
export { LiveScores as Matches };
